import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { buildInstallUrl } from "../services/AttributionService";
import { track } from "../utils/analytics";

const STORAGE_KEY = "android_modal_dismissed_at";
/** Dismiss is hidden for ~1 month, then the prompt can show again */
const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;

const isAndroid = () => /android/i.test(navigator.userAgent);

const shouldShow = () => {
  if (!isAndroid()) return false;
  const dismissedAt = localStorage.getItem(STORAGE_KEY);
  if (!dismissedAt) return true;
  const ts = parseInt(dismissedAt, 10);
  if (Number.isNaN(ts)) return true;
  return Date.now() - ts > ONE_MONTH_MS;
};

export default function AndroidAppModal() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(shouldShow());
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 dark:bg-black/70 backdrop-blur-sm" onClick={dismiss}>
      <div
        className="w-full max-w-sm overflow-hidden rounded-2xl border border-gray-200/80 dark:border-dark-700 bg-white dark:bg-dark-900 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 text-center relative">
          <button
            onClick={dismiss}
            className="absolute top-4 right-4 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:text-dark-400 dark:hover:bg-dark-800 dark:hover:text-dark-100 transition-colors"
            aria-label="Close"
          >
            <X size={22} />
          </button>
          <img src="/logo-240.png" alt="DealHawk" className="h-10 mx-auto mb-4" />
          <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-dark-50">
            Get the DealHawk App
          </h3>
          <p className="mb-6 text-sm leading-relaxed text-gray-500 dark:text-dark-300">
            Download our app from the Google Play Store for push notifications, faster browsing, and more.
          </p>
          <a
            href={buildInstallUrl("android")}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              track("app_store_badge_clicked", { platform: "android", location: "android_app_modal" });
              dismiss();
            }}
            className="mb-3 inline-flex w-full items-center justify-center gap-3 rounded-xl bg-black pl-4 pr-5 py-3 text-white transition-all hover:bg-gray-800 dark:bg-black dark:text-white dark:hover:bg-gray-900"
          >
            <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor">
              <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
            </svg>
            <div className="flex flex-col items-start leading-tight">
              <span className="text-[10px] uppercase tracking-wider opacity-80">Get it on</span>
              <span className="text-lg font-semibold -mt-0.5">Google Play</span>
            </div>
          </a>
          <button
            onClick={dismiss}
            className="w-full rounded-xl py-2.5 font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-dark-300 dark:hover:bg-dark-800 dark:hover:text-dark-100"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}

